import { memo, useEffect, useCallback, useState } from "react";
import Form from "../../components/Form.js";
import Table from "../../components/Table.js";
import { useSnackbar } from "../../utils/index.js";
import Spinner from "../../components/Spinner.js";
import { Box, Typography, Grid2, Button } from "@mui/material";
import { getOwners, getAvailableScrapieAnimals, checkAnimalExistence, createScrapie } from "../../api/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import useListStyles from "../../components/styles/List.js";
import {
    registrationCodeHeaderNoClick,
    flockCodeHeader,
    sexHeaderNoFilter,
    birthDateHeaderNotFilterable,
    regDateHeader,
    cullingDateHeaderNoFilter,
    cullingCodeHeaderNoFilter,
} from "../../components/TableHeaders.js";

const CreateScrapie = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [step, setStep] = useState(1);
    const [testDate, setTestDate] = useState(null);
    const [resultsDate, setResultsDate] = useState(null);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [availableAnimals, setAvailableAnimals] = useState([]);
    const [selectedAnimal, setSelectedAnimal] = useState(null);
    const [scrapieCds, setScrapieCds] = useState([]);
    const [scrapieCodesDropdownValues, setScrapieCodesDropdownValues] = useState([]);
    const [sampleStts, setSampleStts] = useState([]);
    const [sampleStatesDropdownValues, setSampleStatesDropdownValues] = useState([]);

    const selectClasses = useSelectStyles();
    const listClasses = useListStyles();
    const { success, error } = useSnackbar();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);
            setOwnersDropdownValues(
                owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                }))
            );
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, [error]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const selectOwnerDatesFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "date-picker",
            id: "testDate",
            type: "desktop",
            label: "Ηερομηνία Δειγματοληψίας",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "date-picker",
            id: "resultDate",
            type: "desktop",
            label: "Ημερομηνία Αποτελεσμάτων",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const selectAnimalFormContent = [
        {
            customType: "input",
            id: "animal",
            type: "text",
            placeholder: "4-ψήφιος Κωδικός Ζώου",
            value: "",
            disabled: false,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const scrapieFormContent = [
        {
            customType: "input",
            id: "sampleNumber",
            type: "text",
            placeholder: "Αριθμός Δείγματος",
            value: "",
            disabled: false,
        },
        {
            customType: "dropdown",
            id: "sampleState",
            items: sampleStatesDropdownValues,
            placeholder: "Κατάσταση Δείγματος",
            required: false,
            defaultValue: sampleStts[4]?.sampleStateName,
        },
        {
            customType: "dropdown",
            id: "firstResult",
            items: scrapieCodesDropdownValues,
            placeholder: "Πρώτο Αποτέλεσμα",
            required: true,
            defaultValue: scrapieCds[0]?.scrapieName,
        },
        {
            customType: "dropdown",
            id: "secondResult",
            items: scrapieCodesDropdownValues,
            placeholder: "Δεύτερο Αποτέλεσμα",
            required: true,
            defaultValue: scrapieCds[0]?.scrapieName,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const animalTableColumns = [
        registrationCodeHeaderNoClick,
        flockCodeHeader,
        sexHeaderNoFilter,
        birthDateHeaderNotFilterable,
        regDateHeader,
        cullingDateHeaderNoFilter,
        cullingCodeHeaderNoFilter,
    ];

    const handleOwnerSelect = async (values) => {
        setIsLoading(true);
        const selOwner = fetchedOwners.find(
            (owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner
        );
        setSelectedOwner(selOwner);
        setTestDate(values.testDate);
        setResultsDate(values.resultDate);
        const { success: scs, animals, message } = await getAvailableScrapieAnimals(selOwner._id, values.testDate);
        if (scs) {
            setAvailableAnimals(animals);
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    };

    const handleAnimalSelect = async (values) => {
        setIsLoading(true);
        const { success: scs, animal, scrapieCodes, sampleStates, message } = await checkAnimalExistence(selectedOwner?._id, values?.animal);
        if (scs) {
            setSelectedAnimal(animal);
            setScrapieCds(scrapieCodes);
            setScrapieCodesDropdownValues(scrapieCodes.map((code) => ({
                value: code.scrapieName,
                text: code.scrapieName,
            })));
            setSampleStatesDropdownValues(sampleStates.map((state) => ({
                value: state.sampleStateName,
                text: state.sampleStateName,
            })));
            setSampleStts(sampleStates);
            setStep(3);
        } else {
            error(message);
        }

        setIsLoading(false);
    }

    const handleScrapieSubmit = async (values) => {
        setIsLoading(true);
        const ownerId = selectedOwner?._id;
        const animalId = selectedAnimal?._id;
        const sampleCode = values.sampleNumber;
        const sampleDate = testDate;
        const resultDate = resultsDate;
        const sampleState = sampleStts.find((state) => state.sampleStateName === values.sampleState)?.sampleStateCode;
        const result1 = Number(scrapieCds.find((code) => code.scrapieName === values.firstResult)?.scrapieCode);
        const result2 = Number(scrapieCds.find((code) => code.scrapieName === values.secondResult)?.scrapieCode);

        const { success: scs, message } = await createScrapie(
            ownerId,
            animalId,
            sampleCode,
            sampleDate,
            resultDate,
            sampleState,
            result1,
            result2,
        );
        if (scs) {
            success(message);
            setStep(2);
            const { success: sc, animals, message: msg } = await getAvailableScrapieAnimals(selectedOwner?._id, sampleDate);
            if (sc) {
                setAvailableAnimals(animals);
                setStep(2);
            } else {
                error(msg);
            }
        } else {
            error(message);
        }
        setIsLoading(false);
    };

    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <Box className={selectClasses.outerBox}>
                    <Box container flexDirection="column" className={selectClasses.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε την Εκτροφή και τις ημερομηνίες που σας ενδιαφέρουν:
                        </Typography>
                        <Form
                            content={selectOwnerDatesFormContent}
                            onSubmit={handleOwnerSelect}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 2 && (
                <Box>
                    <Form
                        content={selectAnimalFormContent}
                        onSubmit={handleAnimalSelect}
                        toResetForm={false}
                    />
                    {availableAnimals.length > 0 && (
                        <Grid2
                            container
                            spacing={2}
                            sx={{
                                marginTop: "3rem",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                            }}>
                            <Grid2 item xs={12} md={12}>
                                <Typography variant="h6" className={listClasses.form_title} sx={{ display: "flex", justifyContent: "center" }}>
                                    {"Ζώα για τα οποία δεν υπάρχει καταγεγραμμένος έλεγχος SCRAPIE"}
                                </Typography>
                                <Table
                                    data={availableAnimals}
                                    noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν ζώα"}
                                    columns={animalTableColumns}
                                    defaultSorted={[{ id: "birthDate", desc: true }]}
                                />
                            </Grid2>
                        </Grid2>
                    )}
                </Box>
            )}
            {step === 3 && selectedAnimal && (
                <>
                    <Grid2
                        item
                        xs={12}
                        md={12}
                        sx={{
                            mt: "2rem",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h6" className={listClasses.form_title} sx={{ display: "flex", justifyContent: "center" }}>
                            {"Στοιχεία Επιλεγμένου Ζώου"}
                        </Typography>
                        <Table
                            data={[selectedAnimal]}
                            noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν ζώα"}
                            columns={animalTableColumns.slice(0, 5)}
                            defaultSorted={[{ id: "birthDate", desc: true }]}
                            showPageSizeOptions={false}
                        />
                    </Grid2>
                    <Box className={selectClasses.outerBox} sx={{ marginTop: "-5rem" }}>
                        <Box container flexDirection="column" className={selectClasses.innerBox}>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Εισαγωγή Στοιχείων SCARPIE"}
                            </Typography>
                            <Form
                                content={scrapieFormContent}
                                onSubmit={handleScrapieSubmit}
                                toResetForm={true}
                            />
                        </Box>
                    </Box>
                </>
            )}
            {step !== 1 && (
                <Box sx={{ width: "100%", marginTop: step === 2 ? "3rem" : "-10rem", display: "flex", justifyContent: "center" }}>
                    <Button variant="outlined" color="secondary" onClick={() => setStep(step - 1)}>
                        Πίσω
                    </Button>
                </Box>
            )}
        </>
    )
}

export default memo(CreateScrapie);